import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PropertyCard from '@/components/base/PropertyCard';
import { properties } from '@/mocks/properties';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';

const savedIds = ['1', '4', '7'];
const savedProperties = properties.filter(p => savedIds.includes(p.id));

const viewings = [
  { id: '1', property: properties[0], date: 'April 18, 2026', time: '10:30 AM', status: 'confirmed' },
  { id: '2', property: properties[3], date: 'April 22, 2026', time: '2:00 PM', status: 'pending' },
  { id: '3', property: properties[6], date: 'April 25, 2026', time: '3:30 PM', status: 'confirmed' },
];

const recentSearches = [
  { query: 'Luxury Villa, Beverly Hills', date: '2 hours ago' },
  { query: 'Penthouse, Manhattan', date: '1 day ago' },
  { query: '3+ Bed Apartment, Miami', date: '3 days ago' },
  { query: 'Studio, SoHo NYC', date: '5 days ago' },
];

const tabs = ['Saved Properties', 'Recently Viewed', 'Upcoming Viewings', 'Search History'];

const formatTimeAgo = (timestamp: number): string => {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState('Saved Properties');
  const { recentIds, entries, clearHistory } = useRecentlyViewed();

  useEffect(() => {
    document.title = 'My Dashboard | MyAbode';
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (robots) robots.setAttribute('content', 'index, follow');
    };
  }, []);

  const recentProperties = recentIds
    .map(id => {
      const prop = properties.find(p => p.id === id);
      const entry = entries.find(e => e.id === id);
      return prop && entry ? { property: prop, viewedAt: entry.viewedAt } : null;
    })
    .filter(Boolean) as { property: (typeof properties)[0]; viewedAt: number }[];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-[72px]">
        {/* Header */}
        <div className="bg-stone-900 py-10 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-white text-2xl font-black flex-shrink-0">
              A
            </div>
            <div>
              <h1 className="text-2xl font-black text-white">Welcome back, Alex!</h1>
              <p className="text-stone-400 text-sm mt-0.5">Premium Member · Member since January 2025</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Saved Properties', value: '3', icon: 'ri-heart-line', color: 'text-red-500', bg: 'bg-red-50' },
              { label: 'Properties Viewed', value: recentProperties.length > 0 ? String(recentProperties.length) : '0', icon: 'ri-eye-line', color: 'text-amber-500', bg: 'bg-amber-50' },
              { label: 'Agents Contacted', value: '5', icon: 'ri-user-line', color: 'text-green-500', bg: 'bg-green-50' },
              { label: 'Viewings Booked', value: '3', icon: 'ri-calendar-check-line', color: 'text-stone-600', bg: 'bg-stone-100' },
            ].map(stat => (
              <div key={stat.label} className="bg-white rounded-2xl p-5 border border-stone-100">
                <div className={`w-10 h-10 flex items-center justify-center ${stat.bg} rounded-xl mb-3`}>
                  <i className={`${stat.icon} ${stat.color} text-lg`}></i>
                </div>
                <p className="text-3xl font-black text-stone-900">{stat.value}</p>
                <p className="text-stone-400 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-stone-100 rounded-xl p-1 mb-6 w-fit flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab ? 'bg-white text-stone-900' : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {tab}
                {tab === 'Recently Viewed' && recentProperties.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full leading-none">
                    {recentProperties.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'Saved Properties' && (
            <div>
              {savedProperties.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {savedProperties.map(p => (
                    <PropertyCard key={p.id} property={p} saved />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-heart-line text-4xl text-stone-300 mb-3 block"></i>
                  <p className="text-stone-500">No saved properties yet.</p>
                  <Link to="/listings" className="text-amber-500 text-sm mt-2 inline-block hover:underline">Browse Listings</Link>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Recently Viewed' && (
            <div>
              {recentProperties.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-5">
                    <p className="text-stone-500 text-sm">
                      You&apos;ve browsed <strong className="text-stone-800">{recentProperties.length}</strong> {recentProperties.length === 1 ? 'property' : 'properties'} recently
                    </p>
                    <button
                      onClick={clearHistory}
                      className="flex items-center gap-1.5 text-stone-400 hover:text-red-500 text-xs transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Clear history
                    </button>
                  </div>
                  <div className="space-y-3">
                    {recentProperties.map(({ property, viewedAt }) => (
                      <Link
                        key={property.id}
                        to={`/listings/${property.id}`}
                        className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-stone-100 hover:border-amber-200 transition-all group cursor-pointer"
                      >
                        {/* Thumbnail */}
                        <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
                          <img
                            src={property.image}
                            alt={property.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="font-semibold text-stone-800 text-sm truncate group-hover:text-amber-600 transition-colors">
                              {property.title}
                            </h4>
                            {property.isNew && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full flex-shrink-0">New</span>
                            )}
                          </div>
                          <p className="text-stone-400 text-xs flex items-center gap-1 mb-2">
                            <i className="ri-map-pin-line"></i>
                            {property.location}
                          </p>
                          <div className="flex items-center gap-3 text-stone-400 text-xs flex-wrap">
                            <span className="flex items-center gap-1">
                              <i className="ri-hotel-bed-line"></i> {property.bedrooms} Beds
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-drop-line"></i> {property.bathrooms} Baths
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-layout-line"></i> {property.sqft.toLocaleString()} sqft
                            </span>
                          </div>
                        </div>

                        {/* Right side */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span className="font-bold text-stone-900 text-sm">
                            {property.priceType === 'rent'
                              ? `$${property.price.toLocaleString()}/mo`
                              : `$${(property.price / 1000).toFixed(0)}K`}
                          </span>
                          <span className="text-stone-400 text-xs flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {formatTimeAgo(viewedAt)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            property.priceType === 'rent'
                              ? 'bg-stone-100 text-stone-500'
                              : 'bg-amber-50 text-amber-600'
                          }`}>
                            {property.priceType === 'rent' ? 'For Rent' : 'For Sale'}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Browse more CTA */}
                  <div className="mt-6 text-center">
                    <Link
                      to="/listings"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer"
                    >
                      <i className="ri-search-line"></i>
                      Browse More Properties
                    </Link>
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
                    <i className="ri-eye-line text-2xl text-stone-300"></i>
                  </div>
                  <h3 className="text-stone-700 font-semibold mb-1">No properties viewed yet</h3>
                  <p className="text-stone-400 text-sm mb-5">Properties you visit will automatically appear here.</p>
                  <Link
                    to="/listings"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer"
                  >
                    <i className="ri-search-line"></i>
                    Start Browsing
                  </Link>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Upcoming Viewings' && (
            <div className="space-y-4">
              {viewings.map(v => (
                <div key={v.id} className="bg-white rounded-2xl p-5 border border-stone-100 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={v.property.image} alt={v.property.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-stone-800 text-sm">{v.property.title}</p>
                    <p className="text-stone-400 text-xs mt-0.5">{v.property.location}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-stone-500 text-xs">
                        <i className="ri-calendar-line text-amber-500"></i> {v.date}
                      </span>
                      <span className="flex items-center gap-1 text-stone-500 text-xs">
                        <i className="ri-time-line text-amber-500"></i> {v.time}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    v.status === 'confirmed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                  }`}>
                    {v.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'Search History' && (
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {recentSearches.map((s, i) => (
                <div key={i} className={`flex items-center justify-between px-5 py-4 ${i < recentSearches.length - 1 ? 'border-b border-stone-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-50 rounded-lg">
                      <i className="ri-search-line text-stone-400 text-sm"></i>
                    </div>
                    <span className="text-stone-700 text-sm">{s.query}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-stone-400 text-xs">{s.date}</span>
                    <Link to={`/listings?search=${encodeURIComponent(s.query)}`} className="text-amber-500 hover:text-amber-600 text-xs font-medium cursor-pointer">
                      Search again
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DashboardPage;
